//#region IMPORTS
import { useAppSelector } from "../../store/configureStore";
import { AppUser } from "../../models/user";
import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import usePickerManager from "../../hooks/usePickerManager";
import useAdminManager from "../../hooks/useAdminManager";
import BasketIconVtwo from "../icons/BasketIconVtwo";
import useHQmanager from "../../hooks/useHQmanager";
import Avatar from "../avatar/Avatar";
//#endregion

//#region INTERFACE
interface Props {
  avatarClickHandler: () => void;
  user: AppUser | null;
}
//#endregion

export default function DashboardHeaderBarAccount({
  avatarClickHandler,
  user,
}: Props) {
  //#region SETUP
  // Redux
  const { basket } = useAppSelector((state) => state.basket);
  const instoreBasketItems = useAppSelector(
    (state) => state.instoreBasket.items
  );

  // Roles
  const isPicker = usePickerManager(user);
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);

  // Instore basket takes precedence (the two are mutually exclusive)
  const hasInstoreItems = instoreBasketItems.length > 0;
  const itemCount = hasInstoreItems
    ? instoreBasketItems.length
    : basket?.items.length;
  const showBadge = hasInstoreItems || !!basket;
  const basketTo = hasInstoreItems ? "/instore-basket" : "/basket";

  // Total quantity across the active basket — used to detect "adds" so we
  // can fire the ring ripple even when an existing item's qty bumps up.
  const totalQty = hasInstoreItems
    ? instoreBasketItems.reduce((sum, i) => sum + i.quantity, 0)
    : basket?.items.reduce((sum, i) => sum + i.quantity, 0) ?? 0;

  const [ringKey, setRingKey] = useState(0);
  const prevQtyRef = useRef(totalQty);
  useEffect(() => {
    if (totalQty > prevQtyRef.current) setRingKey((k) => k + 1);
    prevQtyRef.current = totalQty;
  }, [totalQty]);
  //#endregion

  //#region RENDER
  return (
    <div className="flex items-center gap-x-4 lg:gap-x-6">
      {/* Basket — picker, HQ and admin only see it while they have an instore
          basket going (they impersonate a store to order). */}
      {(isPicker || hqTeam || isAdmin) && !hasInstoreItems ? null : (
        <NavLink to={basketTo}>
          <div className="relative">
            <BasketIconVtwo className="w-8" />
            {showBadge && ringKey > 0 && (
              <motion.span
                key={ringKey}
                className={`pointer-events-none absolute top-0 left-5 my-1 w-4 h-4 rounded-full border-2 ${
                  hasInstoreItems ? "border-purple-600" : "border-red-500"
                }`}
                initial={{ scale: 1, opacity: 0.7 }}
                animate={{ scale: 2.4, opacity: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              />
            )}
            <motion.span
              className={`text-[10px] absolute top-0 left-5 inline-flex items-center justify-center w-4 h-4 text-white font-bold rounded-full my-1 border-1 border-white ${
                hasInstoreItems ? "bg-purple-600" : "bg-red-500"
              }`}
              animate={{ scale: 1.3 }}
              transition={{ type: "spring", stiffness: 80 }}
              key={`${hasInstoreItems ? "instore" : "promo"}-${itemCount}`}
              style={{ display: showBadge ? "inline-flex" : "none" }}
            >
              {itemCount}
            </motion.span>
          </div>
        </NavLink>
      )}

      {/* Separator */}
      <div
        className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"
        aria-hidden="true"
      />

      {/* Profile */}
      <div className="relative">
        <div className="-m-1.5 flex items-center p-1.5 gap-4">
          <span className="hidden lg:flex lg:items-center">
            <span
              className="ml-4 text-2xl serif leading-6 text-gray-900"
              aria-hidden="true"
            >
              {user?.name}
            </span>
          </span>

          {user && (
            <div className="hover:cursor-pointer">
              <Avatar name={user.name} clickHandler={avatarClickHandler} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
  //#endregion
}
